import type { FocusEvent, ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import ReactAsyncSelect from 'react-select-new/async';

import { Icon } from '@anyroad/icon';
import {
  flip,
  offset,
  Popover,
  PopoverContent,
  PopoverTrigger,
  shift,
} from '@anyroad/popover';
import { colors } from '@anyroad/theme';

import {
  getSelectStyles,
  InputError,
  InputErrorWrapper,
  Label,
  RightIcon,
  SelectWrapper,
  Subtitle,
  Subtext,
  ValidationTooltipContent,
  WarningIconTrigger,
} from './styles';
import type { LocationOption } from './styles';

export type { LocationOption } from './styles';

export type ValidationState = 'idle' | 'invalid' | 'valid';

export interface AsyncSelectProps {
  classNamePrefix?: string;
  errorMessage?: string;
  label: string;
  loadOptions(value: string): Promise<LocationOption[]>;
  minInputLength?: number;
  onChange?: (value: string, option?: LocationOption | null) => void;
  placeholder: string;
  subtext: string;
  subtitle?: string;
  validationState?: ValidationState;
  value?: LocationOption | null;
  warningTooltip?: ReactNode;
}

export const AsyncSelect = ({
  classNamePrefix,
  errorMessage,
  label,
  loadOptions,
  minInputLength = 3,
  placeholder,
  subtext,
  subtitle,
  onChange,
  validationState: validationStateProp,
  value: valueProp,
  warningTooltip,
}: AsyncSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [asyncHasOptions, setAsyncHasOptions] = useState(false);
  const loadGenRef = useRef(0);
  const [internalSelected, setInternalSelected] =
    useState<LocationOption | null>(null);
  const selected = valueProp !== undefined ? valueProp : internalSelected;
  const [internalValidationState, setInternalValidationState] =
    useState<ValidationState>('idle');

  const validationState = validationStateProp ?? internalValidationState;

  const styles = useMemo(() => getSelectStyles(), []);

  const wrappedLoadOptions = useCallback(
    async (value: string) => {
      const q = value.trim();
      if (q.length < minInputLength) {
        loadGenRef.current += 1;
        setAsyncHasOptions(false);
        setMenuOpen(false);
        return [];
      }
      const gen = (loadGenRef.current += 1);
      const opts = await loadOptions(q);
      if (gen !== loadGenRef.current) {
        return [];
      }
      const has = opts.length > 0;
      setAsyncHasOptions(has);
      if (has) {
        setMenuOpen(true);
      } else {
        setMenuOpen(false);
      }
      return opts;
    },
    [loadOptions, minInputLength]
  );

  const handleBlur = useCallback(() => {
    if (validationStateProp !== undefined) return;
    const q = (selected?.label ?? inputValue).trim();
    if (!q) {
      setInternalValidationState('idle');
      return;
    }
    if (selected) {
      setInternalValidationState('valid');
      return;
    }
    const looksValid = /\d/u.test(q) && /[a-zA-Z]/u.test(q);
    setInternalValidationState(looksValid ? 'valid' : 'invalid');
  }, [selected, inputValue, validationStateProp]);

  const handleChange = useCallback(
    (opt: LocationOption | null) => {
      onChange?.(opt?.value ?? '', opt);
      setInternalSelected(opt ?? null);
      setInputValue('');
      if (validationStateProp === undefined) {
        setInternalValidationState(opt ? 'valid' : 'idle');
      }
    },
    [onChange, validationStateProp]
  );

  const handleInputChange = useCallback(
    (val: string, meta: { action: string }) => {
      if (meta.action === 'input-change') {
        if (selected && val === selected.label && inputValue === '') {
          return '';
        }
        if (val === '') {
          onChange?.('', null);
        } else {
          onChange?.(val ?? '');
        }
        setInternalSelected(null);
        setInputValue(val);
        if (validationStateProp === undefined) {
          setInternalValidationState('idle');
        }
      } else if (
        (meta.action === 'menu-close' || meta.action === 'input-blur') &&
        val === ''
      ) {
        return inputValue;
      }
      return val;
    },
    [inputValue, onChange, selected, validationStateProp]
  );

  const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
    const { target } = event;
    if (target instanceof HTMLInputElement && target.value.length > 0) {
      const len = target.value.length;
      target.setSelectionRange(len, len);
    }
  }, []);

  const icon =
    validationState === 'valid'
      ? 'OutlinedCheck'
      : validationState === 'invalid'
        ? 'Warning'
        : undefined;

  const iconFill =
    validationState === 'valid'
      ? colors.success
      : validationState === 'invalid'
        ? colors.warningOrange
        : undefined;

  return (
    <div style={{ width: '100%' }}>
      <Label className="async-select-label-component">{label}</Label>
      {Boolean(subtitle?.trim()) && (
        <Subtitle className="async-select-subtitle-component">
          {subtitle}
        </Subtitle>
      )}
      <InputErrorWrapper>
        <SelectWrapper className="async-select-wrapper-component">
          <ReactAsyncSelect<LocationOption, false>
            aria-invalid={errorMessage ? 'true' : 'false'}
            aria-label={label}
            cacheOptions
            className="async-select-component"
            classNamePrefix={classNamePrefix}
            components={{ DropdownIndicator: null, IndicatorSeparator: null }}
            defaultOptions={false}
            inputValue={inputValue}
            loadOptions={wrappedLoadOptions}
            menuIsOpen={Boolean(menuOpen && asyncHasOptions)}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : undefined
            }
            menuPosition="fixed"
            noOptionsMessage={() => null}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            onInputChange={handleInputChange}
            onMenuClose={() => setMenuOpen(false)}
            onMenuOpen={() => setMenuOpen(true)}
            placeholder={placeholder}
            styles={styles}
            value={selected}
          />
          {icon ? (
            validationState === 'invalid' && warningTooltip ? (
              <Popover
                middleware={[
                  offset(2),
                  flip({
                    crossAxis: false,
                    fallbackAxisSideDirection: 'none',
                    mainAxis: false,
                  }),
                  shift({ padding: 5 }),
                ]}
                placement="top"
              >
                <PopoverTrigger asChild>
                  <WarningIconTrigger
                    aria-label="Address validation warning"
                    type="button"
                  >
                    <Icon
                      className="warning-icon"
                      fill={iconFill}
                      height="16px"
                      name="Warning"
                      width="16px"
                    />
                  </WarningIconTrigger>
                </PopoverTrigger>
                <PopoverContent style={{ zIndex: 10050 }}>
                  <ValidationTooltipContent>
                    {warningTooltip}
                  </ValidationTooltipContent>
                </PopoverContent>
              </Popover>
            ) : (
              <RightIcon
                className="right-icon"
                fill={iconFill}
                height="16px"
                name={icon}
                width="16px"
              />
            )
          ) : null}
        </SelectWrapper>
        {Boolean(errorMessage) && (
          <InputError className="async-select-error-component">
            {errorMessage}
          </InputError>
        )}
      </InputErrorWrapper>
      {Boolean(subtext.trim()) && (
        <Subtext className="async-select-subtext-component">{subtext}</Subtext>
      )}
    </div>
  );
};
